import React, { useState } from "react";
import toast from "react-hot-toast";
import { searchAPI } from "../services/api";

const MODES = [{ id:"hybrid",label:"Hybrid",desc:"BM25 + Vector" },{ id:"vector",label:"Vector",desc:"Semantic" },{ id:"keyword",label:"Keyword",desc:"BM25" }];

export default function SearchPage({ documents }) {
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("hybrid");
  const [results, setResults] = useState(null);
  const [allResults, setAllResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [compareAll, setCompareAll] = useState(false);

  const search = async () => {
    if(!query.trim()) return;
    setLoading(true);
    try {
      if(compareAll) {
        const [h,v,k]=await Promise.all([searchAPI.search({query,mode:"hybrid",top_k:5}),searchAPI.search({query,mode:"vector",top_k:5}),searchAPI.search({query,mode:"keyword",top_k:5})]);
        setAllResults({hybrid:h.data.results,vector:v.data.results,keyword:k.data.results}); setResults(null);
      } else {
        const r=await searchAPI.search({query,mode,top_k:10});
        setResults(r.data.results); setAllResults(null);
      }
    } catch { toast.error("Search failed"); }
    finally { setLoading(false); }
  };

  if(!documents.length) return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100%", color:"var(--text-3)", textAlign:"center" }}>
      <div style={{ fontSize:"36px", marginBottom:"12px" }}>🔍</div>
      <p style={{ fontSize:"14px", color:"var(--text-2)" }}>Upload documents first to search them</p>
    </div>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden", background:"var(--bg)" }}>
      <div style={{ padding:"20px 24px", borderBottom:"1px solid var(--border)", flexShrink:0 }}>
        <h2 style={{ fontSize:"18px", fontWeight:700, color:"var(--text-1)", marginBottom:"4px" }}>Search Optimization</h2>
        <p style={{ fontSize:"12px", color:"var(--text-3)", marginBottom:"14px" }}>Search across indexed documents with different retrieval strategies</p>
        <div style={{ display:"flex", gap:"6px", marginBottom:"12px", flexWrap:"wrap" }}>
          {MODES.map(m=>(
            <button key={m.id} onClick={()=>{setMode(m.id);setCompareAll(false);}} className={`mode-pill ${mode===m.id&&!compareAll?"active":""}`}>
              {m.label} <span style={{ fontSize:"10px", opacity:0.65 }}>· {m.desc}</span>
            </button>
          ))}
          <button onClick={()=>setCompareAll(true)} className={`mode-pill ${compareAll?"active":""}`}>Compare All</button>
        </div>
        <div style={{ display:"flex", gap:"10px" }}>
          <input value={query} onChange={e=>setQuery(e.target.value)} onKeyDown={e=>e.key==="Enter"&&search()} placeholder="Search across all indexed documents..." className="input-base" style={{ flex:1 }} />
          <button onClick={search} disabled={loading||!query.trim()}
            style={{ padding:"10px 20px", borderRadius:"12px", background:"var(--accent)", color:"white", border:"none", cursor:"pointer", fontSize:"14px", fontWeight:600, fontFamily:"inherit", opacity:(loading||!query.trim())?0.5:1 }}>
            {loading?"...":"Search →"}
          </button>
        </div>
      </div>

      <div style={{ flex:1, overflowY:"auto", padding:"20px 24px" }}>
        {loading && <div style={{ textAlign:"center", padding:"60px", color:"var(--text-3)", fontSize:"13px" }}>Searching and reranking...</div>}

        {results&&!loading && (
          <div style={{ maxWidth:"760px", margin:"0 auto" }}>
            <p style={{ fontSize:"12px", color:"var(--text-3)", marginBottom:"14px" }}>
              {results.length} results · <span style={{ color:"var(--accent)" }}>{mode} search</span>
              {results[0]?.rerank_score!=null&&<span style={{ color:"var(--text-3)" }}> · CrossEncoder reranked</span>}
            </p>
            {results.map(r=>(
              <div key={r.chunk_id} style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"10px", padding:"14px 16px", marginBottom:"8px" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"8px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
                    <span style={{ width:"22px", height:"22px", borderRadius:"6px", background:"var(--accent-bg)", border:"1px solid var(--accent-border)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"10px", fontWeight:700, color:"var(--accent)" }}>#{r.rank}</span>
                    <span style={{ fontSize:"13px", fontWeight:600, color:"var(--accent)" }}>{r.doc_name}</span>
                  </div>
                  <div style={{ display:"flex", gap:"10px" }}>
                    <span style={{ fontSize:"11px", color:"var(--text-3)", fontFamily:"monospace" }}>{(r.score*100).toFixed(1)}%</span>
                    {r.rerank_score!=null&&<span style={{ fontSize:"11px", color:"var(--accent)", fontFamily:"monospace" }}>↑{r.rerank_score.toFixed(2)}</span>}
                  </div>
                </div>
                <p style={{ fontSize:"13px", color:"var(--text-2)", lineHeight:"1.6" }}>{r.text}</p>
              </div>
            ))}
          </div>
        )}

        {allResults&&!loading && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"16px" }}>
            {MODES.map(m=>(
              <div key={m.id}>
                <div style={{ display:"flex", alignItems:"center", gap:"6px", marginBottom:"10px" }}>
                  <span style={{ fontSize:"13px", fontWeight:600, color:"var(--text-1)" }}>{m.label}</span>
                  <span style={{ fontSize:"11px", color:"var(--text-3)" }}>· {m.desc}</span>
                </div>
                {allResults[m.id].map(r=>(
                  <div key={r.chunk_id} style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"8px", padding:"10px 12px", marginBottom:"6px" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"5px" }}>
                      <span style={{ fontSize:"11px", fontWeight:600, color:"var(--accent)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"70%" }}>{r.doc_name}</span>
                      <span style={{ fontSize:"10px", color:"var(--text-3)", fontFamily:"monospace" }}>{(r.score*100).toFixed(1)}%</span>
                    </div>
                    <p style={{ fontSize:"11px", color:"var(--text-2)", lineHeight:"1.5", overflow:"hidden", display:"-webkit-box", WebkitLineClamp:3, WebkitBoxOrient:"vertical" }}>{r.text}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {!results&&!allResults&&!loading && (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100%", color:"var(--text-3)", textAlign:"center" }}>
            <div style={{ fontSize:"36px", marginBottom:"12px" }}>🔍</div>
            <p style={{ fontSize:"13px" }}>Enter a query above to search</p>
          </div>
        )}
      </div>
    </div>
  );
}